/* Intake items and clarifications → the mail triage table and the threads.
 *
 * Both screens are about correspondence, and both are assembled from more than
 * one resource, because correspondence is where the case and the message meet.
 * A mail row is an intake item's identity — who sent it, when, what it said in
 * the subject line — joined to what the *case* made of it: the classification,
 * the confidence, the catalog verdict, the queue. Neither half is a row on its
 * own.
 *
 * One limitation runs through this file and is worth stating plainly rather than
 * papering over.
 *
 * **The API does not publish message bodies.** `case_item.body_ref` is a pointer
 * into a content store; the row holds a hash and a subject, not the text. So an
 * inbound thread message renders its subject line, and there is no way to render
 * more from this service. The outbound half is different — a clarification's
 * `bodyRendered` *is* stored, because what was sent under an approval has to be
 * reproducible — so sent messages show their full text.
 *
 * That asymmetry is visible on screen, and it is the truth: the cockpit can show
 * you exactly what it sent and only the header of what it received.
 */

import type { components } from '@/shared/api/generated/openapi';
import { classifyOrderType, toQueue, type WireCase } from '@/shared/api/http/map/cases';
import { DASH, formatWhen } from '@/shared/api/http/map/format';
import type { MessageDirection, SpeakerKey, ThreadMessage } from '@/shared/domain/conversation';
import type { MailRecord, MailStatusKey } from '@/shared/domain/intake';
import type { CatalogMatch, CatalogNoteKey, CatalogState } from '@/shared/domain/serviceCatalog';

export type WireCaseItem = components['schemas']['CaseItemModel'];
export type WireClarification = components['schemas']['ClarificationModel'];

/** What the item's own row cannot answer, indexed by the transport.
 *
 * Every field is optional and every lookup degrades. A mail whose case has not
 * loaded is still a mail that arrived, and showing it unclassified is better
 * than dropping it out of a triage screen. */
export interface CommsContext {
  /** Case uuid → the case row. Supplies the classification and the queue. */
  readonly cases?: ReadonlyMap<string, WireCase>;
  /** Customer id → display name. */
  readonly customerNames?: ReadonlyMap<string, string>;
  /** Service-candidate id → offering name. */
  readonly offeringNames?: ReadonlyMap<string, string>;
  readonly now?: Date;
}

/* ---------- mail triage ---------- */

/** The orderability gate's four outcomes → the cockpit's five catalog states.
 *
 * `not-available` and `not-effective` both land on `not-orderable`, and the
 * distinction between them survives in the note rather than being lost: one
 * means the catalog has no such candidate for this customer, the other that the
 * candidate exists but is not launched. Both mean the same thing to the person
 * triaging — this cannot be ordered today — which is what the state is for.
 */
const GATE_STATES: Readonly<Record<string, CatalogState>> = {
  passed: 'matched',
  'no-match': 'not-found',
  'not-available': 'not-orderable',
  'not-effective': 'not-orderable',
};

/** The gate's four outcomes → the sentence the cockpit writes about each.
 *
 * Keys rather than sentences, for the reason the case row carries a state key:
 * a row assembled here sits in React Query's cache until something refetches it,
 * and a language switch is not a refetch. `vocabulary.catalogNote()` does the
 * wording. */
const GATE_NOTES: Readonly<Record<string, CatalogNoteKey>> = {
  passed: 'passed',
  'no-match': 'noMatch',
  'not-available': 'notAvailable',
  'not-effective': 'notEffective',
};

/** How the request resolved against the catalog, in three fields.
 *
 * A case citing an existing service is `existing` whatever the gate said: it is
 * a change to something already delivered, and the gate's question — can this be
 * ordered — is not the question being asked. A case the gate has not run against
 * is `n/a`, not `not-found`; nothing has been checked, and saying "not found"
 * would report a search that never happened.
 */
function toCatalogMatch(wire: WireCase | undefined): CatalogMatch {
  if (!wire) {
    return {
      state: 'n/a',
      offering: DASH,
      noteKey: 'unbound',
      noteToken: 'unbound',
      lifecycle: null,
    };
  }
  if (wire.existingServiceId) {
    return {
      state: 'existing',
      offering: wire.existingServiceId,
      noteKey: 'existing',
      noteToken: 'existing',
      lifecycle: null,
    };
  }

  const result = wire.serviceGateResult;
  if (!result) {
    return {
      state: 'n/a',
      offering: DASH,
      noteKey: 'notRun',
      noteToken: 'not-run',
      lifecycle: null,
    };
  }

  return {
    state: GATE_STATES[result] ?? 'n/a',
    offering: wire.serviceGateCandidate?.id ?? DASH,
    // A gate outcome this cockpit has no sentence for shows its own name, which
    // is the one string identifying what the gate decided.
    noteKey: GATE_NOTES[result] ?? null,
    noteToken: result,
    // The lifecycle *as it stood when the gate ran*, which is the one the
    // verdict was reached under. Today's lifecycle would explain a different
    // decision from the one on the row.
    lifecycle: wire.serviceGateLifecycle ?? null,
  };
}

/** What happened to the item, as the key the triage column reads its word from.
    Not the word: `toMails` runs at fetch time and its rows outlive a language
    switch. */
const ITEM_STATUSES: Readonly<Record<string, MailStatusKey>> = {
  received: 'received',
  bound: 'bound',
  duplicate: 'duplicate',
  quarantined: 'quarantined',
  unbound: 'unbound',
  failed: 'failed',
};

/**
 * Intake items → the mail triage table.
 *
 * The row is an item joined to its case. Everything to the left of `type` comes
 * from the item and is a fact about the message; everything from `type` rightward
 * comes from the case and is a judgement about the request. Keeping the halves
 * distinct is what lets a reader see that an item arrived cleanly and was
 * classified badly, which is the failure this screen exists to catch.
 *
 * An unbound item — one no case claimed — still gets a row, with its
 * classification columns empty and its status saying why. That backlog is the
 * point of the screen, not an edge case to filter out.
 */
export function toMails(
  items: readonly WireCaseItem[],
  context: CommsContext = {},
): readonly MailRecord[] {
  const now = context.now ?? new Date();

  return items.map((item) => {
    const caseId = item.case?.id;
    const wire = caseId ? context.cases?.get(caseId) : undefined;
    const customerId = wire?.customer?.id;
    const candidateId = wire?.serviceGateCandidate?.id;

    return {
      caseItemId: item.id,
      canReanalyse: item.sourceType === 'email' && !!item.bodyRef
        && !['duplicate', 'quarantined'].includes(item.processingStatus)
        && (!caseId || (!!wire && !wire.stopFlag
          && ['parked', 'service-exception'].includes(wire.routingQueue))),
      id: item.messageCode,
      messageId: item.sourceId,
      from: item.senderAddress ?? DASH,
      // Null rather than a stand-in phrase: "(no subject)" is prose, and prose
      // written here is prose in whatever language the fetch happened in.
      subject: item.subject ?? null,
      /* A name, or nothing — never the id, exactly as `map/cases.ts` does it.
       *
       * The three cases are distinct and lead somewhere different: the customer
       * resolved, the case is anchored to a tenant this screen could not name,
       * or no case has claimed the item at all. The middle one used to print the
       * uuid, which puts a system identifier on screen and hands the reader a
       * string they cannot search a contract by. `tenant` below is what tells
       * the second and the third apart, and `vocabulary.customerName()` has a
       * different word for each. */
      customer: (customerId ? context.customerNames?.get(customerId) : undefined) ?? null,
      tenant: customerId ?? null,
      received: formatWhen(item.receivedAt, now),
      type: wire ? classifyOrderType(wire) : 'UNCLASSIFIED',
      confidence: wire?.confidenceScore ?? 0,
      requested:
        (candidateId ? context.offeringNames?.get(candidateId) : undefined) ?? DASH,
      catalog: toCatalogMatch(wire),
      routed: wire ? toQueue(wire.routingQueue) : 'parked',
      case: wire?.caseReference ?? DASH,
      statusKey: ITEM_STATUSES[item.processingStatus] ?? null,
      statusToken: item.processingStatus,
    };
  });
}

/* ---------- threads ---------- */

/** Two letters for the avatar. Names give their initials, addresses give the
 *  first two letters of the local part, and anything unreadable — including a
 *  message that named nobody at all — gives `??`, which reads correctly as "we
 *  do not know who this is". */
function initialsOf(who: string | null): string {
  if (who === null) return '??';
  const local = who.includes('@') ? who.slice(0, who.indexOf('@')) : who;
  const words = local.split(/[\s._-]+/).filter(Boolean);
  if (words.length === 0) return '??';
  if (words.length === 1) return (words[0]?.slice(0, 2) ?? '??').toUpperCase();
  return `${words[0]?.[0] ?? ''}${words[1]?.[0] ?? ''}`.toUpperCase();
}

/** Who an inbound item is from: the address it came from, or which of the two
 *  ways of having no address this is. The word for each is the cockpit's, so it
 *  is left as a key for `vocabulary.speaker()` to write at render. */
function senderOf(item: WireCaseItem): SpeakerKey {
  return item.sourceType === 'manual-entry' ? 'manualEntry' : 'unknownSender';
}

function toInbound(item: WireCaseItem, now: Date): ThreadMessage {
  // Empty counts as absent on both, as it did before: a stored empty string is
  // an address nobody can reply to and a subject nobody can read.
  const who = item.senderAddress ? item.senderAddress : null;
  return {
    dir: 'in',
    who,
    ...(who === null ? { whoKey: senderOf(item) } : {}),
    initials: initialsOf(who),
    at: formatWhen(item.receivedAt, now),
    /* The subject line, because that is what is stored. `body_ref` points into
       a content store the API does not read from, so there is no fuller text to
       be had — and a message with no subject says so, in a sentence written at
       render, rather than rendering as a blank bubble that looks like a
       rendering fault. */
    text: item.subject ? item.subject : null,
    ...(item.subject ? {} : { source: item.sourceType }),
  };
}

/** Which side of the conversation a clarification sits on.
 *
 * A clarification that was approved and sent is `out` however it was drafted —
 * it left under a person's approval, and that is what the recipient received.
 * One still in draft is shown as the assistant speaking when the assistant wrote
 * it, so a reader can tell a proposed sentence from a sent one at a glance. That
 * distinction is the whole reason `MessageDirection` has four values instead of
 * two.
 */
function directionOf(wire: WireClarification): MessageDirection {
  if (wire.status === 'sent' || wire.status === 'approved') return 'out';
  return wire.generatedByType === 'ai' ? 'ai' : 'out';
}

function toOutbound(wire: WireClarification, now: Date): ThreadMessage {
  const sent = wire.status === 'sent' || wire.status === 'approved';
  return {
    dir: directionOf(wire),
    who: wire.generatedBy,
    // A flag, not a suffix. "(draft)" is the cockpit's word and belongs to the
    // reader's language; the name beside it is the author's own and does not.
    ...(sent ? {} : { draft: true }),
    initials: initialsOf(wire.generatedBy),
    at: formatWhen(wire.createdAt, now),
    // The rendered body, which is stored precisely so that what was sent can be
    // read back. The subject leads it, because a thread of bodies with no
    // subjects loses track of what each message was answering.
    text: wire.subjectRendered
      ? `${wire.subjectRendered}\n\n${wire.bodyRendered}`
      : wire.bodyRendered,
  };
}

/** The instant a message is filed under, for ordering. Unparseable timestamps
    sort to the front rather than throwing — a message with a broken date is
    still a message, and losing it would be worse than misplacing it. */
function instantOf(iso: string): number {
  const at = Date.parse(iso);
  return Number.isNaN(at) ? 0 : at;
}

/**
 * Items and clarifications → one thread per case, oldest first.
 *
 * Keyed by **case reference**, not by uuid: `threadFor()` in
 * `shared/domain/templates.ts` looks a thread up by `CaseRecord.id`, and that is
 * the reference. A case whose reference the transport could not resolve is left
 * out rather than filed under its uuid, where nothing would ever find it.
 *
 * Only the current draft of each clarification is shown. Superseded drafts are
 * the edit history of one message, and replaying them in a conversation would
 * read as the cockpit having sent the customer four slightly different letters.
 *
 * Ordering is by timestamp across both sources, so a reply and the draft that
 * prompted it interleave the way they happened rather than appearing as two
 * blocks.
 */
export function toThreads(
  items: readonly WireCaseItem[],
  clarifications: ReadonlyMap<string, readonly WireClarification[]>,
  references: ReadonlyMap<string, string>,
  context: CommsContext = {},
): Readonly<Record<string, readonly ThreadMessage[]>> {
  const now = context.now ?? new Date();
  const dated = new Map<string, { readonly at: number; readonly message: ThreadMessage }[]>();

  const push = (caseId: string, at: number, message: ThreadMessage): void => {
    const reference = references.get(caseId);
    if (!reference) return;
    const thread = dated.get(reference) ?? [];
    thread.push({ at, message });
    dated.set(reference, thread);
  };

  for (const item of items) {
    if (!item.case?.id) continue;
    push(item.case.id, instantOf(item.receivedAt), toInbound(item, now));
  }

  for (const [caseId, drafts] of clarifications) {
    for (const draft of drafts) {
      if (!draft.isCurrent) continue;
      push(caseId, instantOf(draft.createdAt), toOutbound(draft, now));
    }
  }

  const threads: Record<string, readonly ThreadMessage[]> = {};
  for (const [reference, entries] of dated) {
    threads[reference] = entries
      .sort((a, b) => a.at - b.at)
      .map((entry) => entry.message);
  }
  return threads;
}
